<template>
    <div
        class="qr-call h-full"
        data-test="qr-call"
    >
        <div
            v-show="isLive"
            class="qr-call__live"
        >
            <div class="qr-call__stage">
                <VideoStage
                    ref="videoStage"
                    :badge-color="badgeColor"
                    :display-name="displayName"
                    :duration="stage === QR_CALL_STAGE.IN_CALL ? duration : ''"
                    :remote-video-on="remoteVideoOn"
                    :cam-on="camOn"
                    :remote-camera-off-label="stage === QR_CALL_STAGE.IN_CALL ? $t('qr.call.remote_camera_off') : ''"
                >
                    <CallStage
                        v-if="!isFinal"
                        :stage="stage"
                        :display-name="displayName"
                        :reconnecting="reconnecting"
                    />
                </VideoStage>

                <div class="qr-call__top">
                    <UnlockBanner v-if="lockOpened" />
                    <div
                        v-if="audioOnly"
                        class="qr-call__notice"
                    >
                        {{ $t('qr.call.audio_only') }}
                    </div>
                </div>
            </div>

            <div class="qr-call__bottom">
                <button
                    v-if="autoplayBlocked"
                    class="btn btn-primary w-full mb-4"
                    data-test="qr-enable-sound"
                    @click="resumeMedia"
                >
                    {{ $t('qr.call.resume_call') }}
                </button>

                <CallControls
                    :mic-on="micOn"
                    :mic-allowed="micAllowed"
                    :cam-on="camOn"
                    :audio-only="audioOnly"
                    @toggle-mic="toggleMic"
                    @toggle-cam="toggleCam"
                    @hang-up="endCall"
                />
            </div>
        </div>

        <CallStage
            v-if="isFinal"
            :stage="stage"
            :display-name="displayName"
            :error-code="errorCode"
            @back="goToSearch"
            @retry="startCall"
        />
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useQrCall } from '../../composables/qr/useQrCall';
import VideoStage from '../../components/qr/VideoStage';
import CallStage from '../../components/qr/CallStage';
import CallControls from '../../components/qr/CallControls';
import UnlockBanner from '../../components/qr/UnlockBanner';
import { useQrVisitorContext } from '../../composables/qr/useQrVisitorContext';
import { QR_CALL_STAGE, QR_ROUTES, QR_RTM_CONFIG_PROVIDER } from '../../constants/qr';

// Declined/missed/ended keep the dark call surface while the redirect happens, avoiding a white flash
const LIVE_STAGES = [
    QR_CALL_STAGE.CONNECTING, QR_CALL_STAGE.RINGING, QR_CALL_STAGE.IN_CALL,
    QR_CALL_STAGE.DECLINED, QR_CALL_STAGE.MISSED, QR_CALL_STAGE.ENDED,
];
const NOTICE_BY_STAGE = {
    [QR_CALL_STAGE.DECLINED]: 'qr.call.declined',
    [QR_CALL_STAGE.MISSED]: 'qr.call.no_answer',
};

const route = useRoute();
const router = useRouter();
const store = useStore();
const visitor = useQrVisitorContext();
const videoStage = ref(null);

const {
    stage, endedByResident, ringTimedOut, duration, micOn, micAllowed, camOn, audioOnly, remoteVideoOn, reconnecting,
    autoplayBlocked, lockOpened, errorCode, start, hangup, toggleMic, toggleCam, resumeMedia,
} = useQrCall({
    entranceId: route.params.entranceId,
    residentId: route.params.residentId,
    getRtmConfig: inject(QR_RTM_CONFIG_PROVIDER, null),
    onOutsideGeofence: error => {
        visitor.invalidateVerification(error);
        router.replace({ name: QR_ROUTES.LOCATION, params: { entranceId: route.params.entranceId } });
    },
});

const selectedResident = computed(() => store.getters['qr/selectedResident']);
const badgeColor = computed(() => selectedResident.value?.badgeColor || '');
const displayName = computed(() => selectedResident.value?.displayName || '');
const isLive = computed(() => LIVE_STAGES.includes(stage.value));
const isFinal = computed(() => stage.value === QR_CALL_STAGE.ERROR);
let answered = false;

function startCall() {
    answered = false;
    return start(videoStage.value.getElements());
}

function endCall() {
    return hangup();
}

// replace() so the browser back button never re-enters (and re-dials) the call route
function goToSearch() {
    router.replace({ name: QR_ROUTES.SEARCH, params: { entranceId: route.params.entranceId } });
}

function goToHome() {
    router.replace({ name: QR_ROUTES.HOME, params: { entranceId: route.params.entranceId } });
}

watch(stage, value => {
    if (value === QR_CALL_STAGE.IN_CALL) {
        answered = true;
        return;
    }
    if (NOTICE_BY_STAGE[value]) {
        store.dispatch('qr/showNotice', {
            key: value === QR_CALL_STAGE.MISSED && ringTimedOut.value
                ? 'qr.call.max_call_time'
                : NOTICE_BY_STAGE[value],
            params: { name: displayName.value },
        });
        goToSearch();
        return;
    }
    if (value === QR_CALL_STAGE.ENDED) {
        if (answered) {
            if (endedByResident.value) {
                store.dispatch('qr/showNotice', {
                    key: 'qr.call.ended',
                    params: { name: displayName.value },
                });
            }
            goToHome();
            return;
        }
        goToSearch();
    }
}, { flush: 'sync' });

onMounted(startCall);
</script>

<style scoped>
.qr-call {
    font-family: sofia-pro, Helvetica, Arial, sans-serif;
    letter-spacing: 0;
}

.qr-call__live {
    position: fixed;
    inset: 0;
    bottom: var(--qr-connection-space, 0px);
    z-index: 10;
    display: grid;
    grid-template-rows: minmax(0, 1fr) auto;
    background: #101010;
    overflow: hidden;
}

.qr-call__stage {
    position: relative;
    min-height: 0;
}

.qr-call__top {
    position: absolute;
    top: calc(16px + env(safe-area-inset-top));
    left: calc(16px + env(safe-area-inset-left));
    right: calc(110px + env(safe-area-inset-right) + 16px);
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.qr-call__notice {
    padding: 8px 12px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    font-size: 14px;
}

.qr-call__bottom {
    padding: 24px calc(16px + env(safe-area-inset-right)) calc(24px + env(safe-area-inset-bottom)) calc(16px + env(safe-area-inset-left));
    background: #101010;
}
</style>
